import { useMatch } from 'react-router-dom'

import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import pathName from '../../../routing/pathName.constant'
import ANTDButton from '../../../shared/antd/ANTDButton'
import ANTDCheckbox from '../../../shared/antd/ANTDCheckbox'
import { addressFormat } from '../../../utils'
import { childUsers, userWiseRole } from '../../../utils/constant'
import { dateFormat } from '../../../utils/dateFormat'
import { noImage } from '../../../utils/icons'
import { include, isEqual, notEqual, ternary } from '../../../utils/javascript'
import { departmentOptions } from '../user.description'

const userColumns = ({
  permission,
  handleView,
  selectedUsers,
  handleSelectChange,
  handleDAssociate,
  roleId,
  handleEdit,
  isBuilding,
  removeEditBtn,
  showSelectedColumns,
  showSendReminder,
  handleSendReminder,
  handlePreviousReminders,
}) => {
  const matchRoute = useMatch(pathName.PUBLIC_JOB_DETAILS)
  const isPublicPage = Boolean(matchRoute)
  const { t } = useTranslations()
  const { selector } = useRedux()
  const { profile_details } = selector(state => state.user)

  const {
    consumer,
    producer,
    dataEntry,
    categoryUser,
    salesUser,
    operationsUser,
    scrappingFacilityEntryUser,
    superUser,
    dealer,
    collectionCenter,
  } = userWiseRole
  const isChildUser = include(childUsers, roleId)

  const actionButtons = rowData => {
    const allParentId = rowData?.allParentId
    const showReminderButton = showSendReminder && !isBuilding
    const showEditBtn =
      !include(
        [
          superUser,
          dataEntry,
          categoryUser,
          salesUser,
          operationsUser,
          scrappingFacilityEntryUser,
        ],
        roleId,
      ) || include(allParentId, profile_details?.id)

    return (
      <div className="flex-nowrap d-flex">
        <ANTDButton className="bg-view" onClick={() => handleView(rowData)}>
          {t('btn_View')}
        </ANTDButton>
        {!removeEditBtn && ((permission && showEditBtn) || isBuilding) && (
          <ANTDButton className="bg-edit" onClick={() => handleEdit(rowData)}>
            {t('btn_Edit')}
          </ANTDButton>
        )}
        {handleDAssociate && !isBuilding && (
          <ANTDButton
            className="bg-danger"
            onClick={() => handleDAssociate(rowData)}
          >
            {t('btn_DisAssociate')}
          </ANTDButton>
        )}
        {showReminderButton && (
          <>
            <ANTDButton
              type="primary"
              className="btn"
              onClick={() => handleSendReminder(rowData)}
            >
              {t('user_sendReminder')}
            </ANTDButton>
            <ANTDButton
              type="primary"
              className="btn ml-5"
              onClick={() => handlePreviousReminders(rowData)}
            >
              {t('user_previousReminders')}
            </ANTDButton>
          </>
        )}
      </div>
    )
  }

  const getColumnTitle = type => {
    switch (type) {
      case 'user_BusinessName':
        const businessName =
          showSelectedColumns && isEqual(roleId, producer)
            ? 'job_MakerBusinessName'
            : 'user_BusinessName'
        return t(businessName)

      default:
        return null
    }
  }

  const column = [
    {
      title: null,
      key: 'select',
      render: rowData => {
        const usersList = selectedUsers?.map(v => v?.id)
        return (
          <ANTDCheckbox
            value={rowData}
            onChange={handleSelectChange}
            checked={include(usersList, rowData?.id)}
          />
        )
      },
      hidden: !handleSelectChange,
    },
    {
      title: t('user_ID'),
      dataIndex: 'id',
      key: 'user_ID',
    },
    {
      title: t('user_Image'),
      dataIndex: 'profile',
      className: 'img-td',
      key: 'user_Image',
      render: rowData => {
        return (
          <div className="small-img-wrap">
            <img src={rowData?.fileUrl || noImage} alt={'pic'} />
          </div>
        )
      },
      hidden: isBuilding,
    },
    {
      title: getColumnTitle('user_BusinessName'),
      dataIndex: 'businessName',
      key: 'user_BusinessName',
      className: 'business-name',
      hidden: isChildUser || isBuilding,
      render: rowData => {
        return rowData || '-'
      },
    },
    {
      title: t('job_ConsumerName'),
      dataIndex: 'lastName',
      key: 'user_OwnerName',
      className: 'business-name',
      hidden: !showSelectedColumns,
      render: rowData => {
        return rowData || '-'
      },
    },
    {
      title: t('user_Name'),
      key: 'user_Name',
      render: rowData => {
        return isBuilding ? rowData?.name : rowData?.lastName
      },
    },
    {
      title: t('user_ParentSuperUser'),
      key: 'user_ParentSuperUser',
      dataIndex: 'parentDetails',
      render: rowData => {
        return rowData?.lastName || '-'
      },
      hidden: !include(
        [
          dataEntry,
          categoryUser,
          salesUser,
          operationsUser,
          scrappingFacilityEntryUser,
        ],
        roleId,
      ),
    },
    {
      title: t('user_Department'),
      key: 'user_Department',
      dataIndex: 'departmentName',
      render: rowData => {
        return rowData ? t(departmentOptions[rowData]) : '-'
      },
      hidden: !include(
        [
          dataEntry,
          categoryUser,
          salesUser,
          operationsUser,
          scrappingFacilityEntryUser,
          superUser,
        ],
        roleId,
      ),
    },
    {
      title: t('user_Email'),
      dataIndex: 'emailId',
      key: 'user_Email',
      render: rowData => rowData || '-',
    },
    {
      title: t('job_PAN'),
      dataIndex: 'panNo',
      key: 'job_PAN',
      render: rowData => <div className="w-nowrap">{rowData || '-'}</div>,
      hidden: notEqual(consumer, roleId),
    },
    {
      title: t('user_Contact'),
      dataIndex: isBuilding ? 'contact' : 'phoneNumber',
      key: 'user_Contact',
      render: rowData => <div className="w-nowrap">{rowData || '-'}</div>,
    },
    {
      title: t('user_State'),
      key: 'user_State',
      dataIndex: 'state',
      render: rowData => rowData || '-',
      hidden: !include([dealer, collectionCenter], roleId),
    },
    {
      title: t('user_Address'),
      key: 'user_Address',
      width: '250px',
      className: 'address',
      render: rowData => {
        return addressFormat(rowData)
      },
    },
    {
      title: t('user_DOJ'),
      dataIndex: 'creationDate',
      key: 'user_DOJ',
      render: rowData => {
        const { newDate } = rowData ? dateFormat(rowData) : {}
        return <>{newDate ? newDate : '-'}</>
      },
    },
    {
      title: t('txt_Action'),
      key: 'txt_Action',
      hidden: isPublicPage,
      render: rowData => {
        return <>{actionButtons(rowData)}</>
      },
    },
  ]

  const selectedColumnsList = showSelectedColumns
    ? column?.filter(col =>
        include(['select', ...showSelectedColumns], col?.key),
      )
    : []

  const cardViewFn = ({
    lastName,
    emailId,
    phoneNumber,
    creationDate,
    businessName,
    contact,
    ...user
  }) =>
    [
      { label: 'user_BusinessName', value: businessName, hidden: isChildUser },
      { label: 'user_Name', value: lastName },
      {
        label: 'user_Email',
        value: emailId,
      },
      {
        label: 'job_PAN',
        value: user?.panNo,
        hidden: notEqual(consumer, roleId),
      },
      { label: 'user_Contact', value: phoneNumber },
      {
        label: 'user_Address',
        value: addressFormat(user),
      },
      {
        label: 'user_DOJ',
        value: ternary(creationDate, dateFormat(creationDate)?.newDate, null),
      },
    ].filter(item => !item.hidden)

  return {
    column: showSelectedColumns ? selectedColumnsList : column,
    actionButtons,
    cardViewFn,
  }
}

export default userColumns
